import DOMPurify from "dompurify";
import katex from "katex";
import "katex/dist/katex.min.css";
import React, { useMemo } from "react";

// Whitelisted tags/attrs for rich text inside "text" blocks.
// Only presentational formatting — no scripts, no links, no styles, no images.
const ALLOWED_TAGS = ["b", "strong", "i", "em", "u", "br", "p", "ul", "ol", "li", "span", "div"];
const ALLOWED_ATTR = [];

function sanitize(html) {
  if (html == null) return "";
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: false,
  });
}

function renderKatex(src, block) {
  try {
    return katex.renderToString(String(src ?? "").trim(), {
      throwOnError: false,
      displayMode: block,
      errorColor: "#E11D48",
      strict: "ignore",
      output: "html",
    });
  } catch {
    return String(src ?? "").replace(/</g, "&lt;");
  }
}

/**
 * Walk text nodes of a parsed HTML fragment and substitute `$...$` inline math
 * and `$$...$$` display math with KaTeX-rendered HTML. Rich-text formatting
 * tags (strong/em/u/lists) are preserved as-is.
 *
 * Runs client-side only; guarded when window/document is unavailable.
 */
function injectMathIntoHtml(cleanHtml) {
  if (typeof document === "undefined" || !cleanHtml.includes("$")) return cleanHtml;
  const container = document.createElement("div");
  container.innerHTML = cleanHtml;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);

  const regex = /(\$\$[\s\S]+?\$\$|\$[^\n$]+?\$)/g;
  for (const node of textNodes) {
    const t = node.nodeValue;
    if (!t || !t.includes("$")) continue;
    regex.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let last = 0;
    let matched = false;
    let m;
    while ((m = regex.exec(t)) !== null) {
      matched = true;
      if (m.index > last) frag.appendChild(document.createTextNode(t.slice(last, m.index)));
      const raw = m[0];
      const isBlock = raw.startsWith("$$");
      const src = isBlock ? raw.slice(2, -2) : raw.slice(1, -1);
      const wrap = document.createElement(isBlock ? "div" : "span");
      wrap.innerHTML = renderKatex(src, isBlock);
      frag.appendChild(wrap);
      last = m.index + raw.length;
    }
    if (matched) {
      if (last < t.length) frag.appendChild(document.createTextNode(t.slice(last)));
      node.parentNode.replaceChild(frag, node);
    }
  }
  return container.innerHTML;
}

/**
 * Renders a text block that MAY contain rich-text HTML (bold/italic/underline/lists).
 * Also plain legacy strings: newlines convert to <br>.
 * Inline `$...$` and display `$$...$$` math is rendered via KaTeX — same behaviour
 * as choice text — so authors can freely mix formatting and math in the question body.
 */
export default function RichText({ html, className = "" }) {
  const finalHtml = useMemo(() => {
    if (!html) return "";
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(String(html));
    const raw = looksLikeHtml ? html : String(html).replace(/\n/g, "<br>");
    return injectMathIntoHtml(sanitize(raw));
  }, [html]);

  if (!finalHtml) return null;
  return (
    <div
      className={`prose-rt ${className}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
}

export function sanitizeRichText(html) {
  return sanitize(html);
}
